import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APTITUDE_RANKS, getAptitudeBadgeStyle, getAptitudeImage } from '../../../../utils/aptitudeRanks';


// --- VISUAL COMPONENTS ---

// 1. Aptitude Badge (Circle with Letter)
const AptitudeBadge = ({ rank, size = "md" }) => {
  const sizeClasses = size === "sm"
    ? "w-8 h-8 p-1.5 ring-1 ring-offset-1"
    : "w-10 h-10 sm:w-11 sm:h-11 p-2 ring-2 ring-offset-2";

  // Use centralized logic
  const imgSrc = getAptitudeImage(rank);

  return (
    <div className={`${sizeClasses} rounded-full flex items-center justify-center shadow-sm dark:ring-offset-slate-800 transition-colors duration-200 ${getAptitudeBadgeStyle(rank)}`}>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={rank}
          className="w-full h-full object-contain drop-shadow-sm filter brightness-105"
          draggable="false"
        />
      ) : (
        <span className="font-black text-gray-400">{rank || '-'}</span>
      )}
    </div>
  );
};

// 2. SVG Icons (Inline replacement for Lucide)
const MapPinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
);
const NavigationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
);
const TimerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" x2="14" y1="2" y2="2" /><line x1="12" x2="15" y1="14" y2="11" /><circle cx="12" cy="14" r="8" /></svg>
);
const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
);




const AptitudePanel = ({ aptitudes, onAptitudeChange }) => {
  const { t } = useTranslation();
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Define categories with translation keys
  const categories = {
    [t('AptitudePanel.category_track')]: { keys: ['turf', 'dirt'], icon: MapPinIcon },
    [t('AptitudePanel.category_distance')]: { keys: ['short_distance', 'mile', 'medium_distance', 'long_distance'], icon: NavigationIcon },
    [t('AptitudePanel.category_style')]: { keys: ['runner', 'leader', 'betweener', 'chaser'], icon: TimerIcon }
  };

  // Click Outside Handler (Replaces Overlay)
  React.useEffect(() => {
    const handleGlobalClick = (event) => {
      if (!openDropdownId) return;

      // Check if click was inside ANY aptitude wrapper (not just the open one)
      // This allows the specific onClick handlers to manage the toggle logic
      // without this global listener interfering (closing it before toggle).
      const closestAny = event.target.closest('[data-aptitude-id]');

      if (closestAny) {
        return;
      }

      // Only close if clicked strictly outside ALL aptitude panels
      setOpenDropdownId(null);
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [openDropdownId]);

  const toggleDropdown = (id) => {
    if (openDropdownId === id) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(id);
    }
  };

  const handleSelect = (key, rank) => {
    if (onAptitudeChange) onAptitudeChange(key, rank);
    setOpenDropdownId(null);
  };

  const AptitudeItem = ({ aptKey }) => {
    const rank = aptitudes ? aptitudes[aptKey] : '-';
    const isOpen = openDropdownId === aptKey;
    const label = t(`AptitudeValues.${aptKey}`); // Access translations via key

    return (
      <div
        className={`relative group ${isOpen ? 'z-[100]' : 'z-10'}`}
        data-aptitude-id={aptKey} // Identifier for click-outside logic
      >

        {/* Trigger Card */}
        <div
          onClick={() => toggleDropdown(aptKey)}
          className={`flex items-center justify-between p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-slate-700/50 border cursor-pointer transition-all duration-200
            ${isOpen
              ? 'border-[#00c541] dark:border-[#00c541] ring-2 ring-[#00c541]/20 relative'
              : 'border-gray-200 dark:border-slate-600 hover:border-[#00c541] dark:hover:border-[#00c541] hover:shadow-lg hover:-translate-y-0.5'
            }`}
        >
          <span className="text-xs sm:text-sm font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
            {label}
            {isOpen && <div className="text-[#00c541]"><ChevronDownIcon /></div>}
          </span>

          {/* Badge Display */}
          <div className="flex-shrink-0">
            <AptitudeBadge rank={rank} />
          </div>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 min-w-[14rem] w-max bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-100 dark:border-slate-600 z-[100] p-3 animate-in fade-in zoom-in-95 duration-200 origin-top">
            <div className="grid grid-cols-4 gap-2">
              {APTITUDE_RANKS.map(r => (
                <button
                  key={r}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(aptKey, r);
                  }}
                  className={`flex flex-col items-center justify-center p-1 rounded-full aspect-square transition-all
                    ${rank === r
                      ? 'bg-green-50 dark:bg-green-900/30 ring-2 ring-[#00c541]'
                      : 'hover:bg-gray-100 dark:hover:bg-slate-700'
                    }
                  `}
                >
                  <AptitudeBadge rank={r} size="sm" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-3xl shadow-xl dark:shadow-black/20 p-6 sm:p-8 border border-gray-100 dark:border-slate-700 transition-colors duration-300 w-full relative ${openDropdownId ? 'z-[110]' : 'z-0'}`}>

      {/* Header */}
      <div className="flex items-center gap-3 mb-8 border-b border-gray-100 dark:border-slate-700 pb-4 relative z-0">
        <div className="w-1.5 h-8 bg-[#00c541] rounded-full shadow-[0_0_10px_#00c541]"></div>
        <div>
          <h3 className="text-2xl font-black text-gray-800 dark:text-white tracking-tight">
            {t('AptitudePanel.title')}
          </h3>
          <p className="text-xs text-gray-400 dark:text-slate-400 font-medium">
            {t('AptitudePanel.subtitle')}
          </p>
        </div>
      </div>

      {/* Grid Categories */}
      <div className="flex flex-col gap-8 relative z-0">
        {Object.entries(categories).map(([categoryName, { keys, icon: Icon }]) => (
          <div key={categoryName}>
            <div className="flex items-center gap-2 mb-4 ml-1">
              <div className="p-1.5 bg-[#00c541]/10 rounded-lg text-[#00c541]">
                <Icon />
              </div>
              <h4 className="font-bold text-sm text-gray-500 dark:text-slate-400 uppercase tracking-widest">{categoryName}</h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {keys.map(aptKey => (
                <AptitudeItem key={aptKey} aptKey={aptKey} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AptitudePanel;
// Revisado: 2025-12-17 [I18n Scan]
